/**
 * DocVersionView — Displays a historical (superseded) document version.
 *
 * Shows the full content with a prominent SUPERSEDED banner when viewing
 * an older version. Links to the current effective version.
 *
 * Route: /docs/:slug/version?v=X.Y
 * API: GET /@docs/version?slug=...&v=...
 *
 * Ref: CR-2026-025
 */
import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Loader,
  Icon,
  Breadcrumb,
  Message,
  Label,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';
import markdownComponents from '../markdownComponents';

/** Lazy-load react-markdown + remark-gfm (they're ESM-only) */
let ReactMarkdownComponent = null;
let remarkGfmPlugin = null;
let mdLoaded = false;
let mdLoadPromise = null;

function loadMarkdownDeps() {
  if (mdLoadPromise) return mdLoadPromise;
  mdLoadPromise = Promise.all([
    import('react-markdown'),
    import('remark-gfm'),
  ]).then(([rm, rgfm]) => {
    ReactMarkdownComponent = rm.default;
    remarkGfmPlugin = rgfm.default;
    mdLoaded = true;
  });
  return mdLoadPromise;
}

export default function DocVersionView() {
  const params = useParams();
  const location = useLocation();
  const slug = params.slug || params[0] || '';
  const versionParam = new URLSearchParams(location.search).get('v') || '';
  const [ver, setVer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mdReady, setMdReady] = useState(mdLoaded);

  useEffect(() => {
    if (!mdLoaded) {
      loadMarkdownDeps().then(() => setMdReady(true));
    }
  }, []);

  useEffect(() => {
    if (!slug || !versionParam) return;
    setLoading(true);
    fetchApi(
      `${getApiPath()}/@docs/version?slug=${encodeURIComponent(slug)}&v=${encodeURIComponent(versionParam)}`,
    )
      .then((json) => {
        setVer(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug, versionParam]);

  if (loading || !mdReady) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Loader active inline="centered" content="Loading version..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  if (!ver) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment>Version not found.</Segment>
      </Container>
    );
  }

  const isSuperseded = !!ver.superseded_at;

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '2em', maxWidth: '900px' }}>
      <Helmet title={`v${ver.version} — ${ver.title}`} />

      <Breadcrumb style={{ marginBottom: '1em' }}>
        <Breadcrumb.Section as={Link} to="/docs">Knowledge Base</Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section as={Link} to={`/docs/${slug}`}>
          {ver.title || slug}
        </Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section as={Link} to={`/docs/${slug}/history`}>
          Version History
        </Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>v{ver.version}</Breadcrumb.Section>
      </Breadcrumb>

      {/* SUPERSEDED banner */}
      {isSuperseded && (
        <Message warning icon size="large">
          <Icon name="warning sign" />
          <Message.Content>
            <Message.Header>SUPERSEDED VERSION</Message.Header>
            <p>
              This is version <strong>{ver.version}</strong>, superseded on{' '}
              {new Date(ver.superseded_at).toLocaleDateString()}.
              {ver.superseded_by && (
                <> Replaced by version <strong>{ver.superseded_by}</strong>.</>
              )}
            </p>
            <p>
              The current version is <strong>{ver.current_version}</strong>.{' '}
              <Link to={`/docs/${slug}`}>View current version &rarr;</Link>
            </p>
            <p style={{ color: '#666', fontSize: '0.9em' }}>
              Always obtain the latest version from the web interface.
              Printed or offline copies may be outdated.
            </p>
          </Message.Content>
        </Message>
      )}

      {/* Effective version banner */}
      {!isSuperseded && (
        <Message positive icon>
          <Icon name="check circle" />
          <Message.Content>
            <Message.Header>Current Effective Version</Message.Header>
            This is the current effective version ({ver.version}).
          </Message.Content>
        </Message>
      )}

      <Header as="h1">
        <Header.Content>
          {ver.title}
          <Header.Subheader>
            v{ver.version}
            {ver.author && <> &middot; {ver.author}</>}
          </Header.Subheader>
        </Header.Content>
      </Header>

      {/* Version metadata */}
      <div style={{ marginBottom: '1em' }}>
        <Label size="small">
          <Icon name="calendar" /> Published: {new Date(ver.published_at).toLocaleDateString()}
        </Label>
        <Label size="small">
          <Icon name="user" /> Approved by: {ver.published_by}
        </Label>
        {ver.content_hash && (
          <Label size="small" basic>
            <Icon name="lock" /> SHA-256: {ver.content_hash.substring(0, 12)}...
          </Label>
        )}
      </div>

      {/* Document body */}
      {ver.body_md ? (
        ReactMarkdownComponent && mdReady ? (
          <Segment
            style={{
              fontSize: '1.05em',
              lineHeight: '1.6',
              opacity: isSuperseded ? 0.85 : 1,
            }}
          >
            <ReactMarkdownComponent remarkPlugins={[remarkGfmPlugin]} components={markdownComponents}>
              {ver.body_md}
            </ReactMarkdownComponent>
          </Segment>
        ) : (
          <Segment>
            <pre style={{ whiteSpace: 'pre-wrap' }}>{ver.body_md}</pre>
          </Segment>
        )
      ) : (
        <Segment placeholder textAlign="center">
          <Header icon>
            <Icon name="file text" />
            No body content for this version
          </Header>
        </Segment>
      )}

      {/* Navigation footer */}
      <Segment secondary size="small" style={{ marginTop: '2em' }}>
        <Link to={`/docs/${slug}/history`}>
          <Icon name="history" /> View all versions
        </Link>
        <span style={{ margin: '0 1em' }}>|</span>
        <Link to={`/docs/${slug}`}>
          <Icon name="file text" /> View current version
        </Link>
      </Segment>
    </Container>
  );
}
